import React, { useState, useContext } from "react";
import axios from "axios";
import { AppContext } from "../../../../context/AppContext";
import "../../../../assets/css/patient/patientlayouts/layoutpages/Feedback.css";

const Feedback = () => {
  const { baseUrl } = useContext(AppContext);

  // ---- Section 1: Doctor rating + opinion ----
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [doctorOpinion, setDoctorOpinion] = useState("");

  // ---- Section 2: Experience / suggestion / complaint ----
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [experienceText, setExperienceText] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const MAX_CHARS = 500;

  const ratingTexts = {
    1: "Poor",
    2: "Fair",
    3: "Good",
    4: "Very Good",
    5: "Excellent"
  };

  const feedbackTypes = [
    { id: "suggestion", label: "Suggestion", icon: "💡" },
    { id: "complaint", label: "Complaint", icon: "⚠️" },
    { id: "compliment", label: "Compliment", icon: "🌟" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      alert("Please rate your doctor first");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = localStorage.getItem("auth_token");

      await axios.post(
        `${baseUrl}/feedback`,
        {
          doctor_rating: rating,
          doctor_opinion: doctorOpinion,
          feedback_type: feedbackType,
          experience_text: experienceText
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setShowSuccess(true);
      setRating(0);
      setDoctorOpinion("");
      setFeedbackType("suggestion");
      setExperienceText("");
    } catch (err) {
      const message =
        err.response?.data?.message || "Feedback not submitted, please try again.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pf-container">
      <div className="pf-header">
        <h2>Share Your Feedback</h2>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ==================== SECTION 1: Doctor Rating ==================== */}
        <div className="pf-card">
          <div className="pf-card-title">
            <span className="pf-card-icon">👨‍⚕️</span>
            Rate Your Doctor
          </div>

          <div className="pf-rating-box">
            <div className="pf-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  className={`pf-star ${rating >= star || hoveredRating >= star ? "filled" : ""}`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                >
                  ★
                </span>
              ))}
            </div>
            {(rating > 0 || hoveredRating > 0) && (
              <div className="pf-rating-text">{ratingTexts[hoveredRating || rating]}</div>
            )}
          </div>

          <label className="pf-label">Your written opinion</label>
          <textarea
            className="pf-textarea"
            rows="3"
            maxLength={MAX_CHARS}
            placeholder="Type your comment here..."
            value={doctorOpinion}
            onChange={(e) => setDoctorOpinion(e.target.value)}
          />
          <div className="pf-char-count">{doctorOpinion.length}/{MAX_CHARS}</div>
        </div>

        {/* ==================== SECTION 2: Experience / Suggestion ==================== */}
        <div className="pf-card">
          <div className="pf-card-title">
            <span className="pf-card-icon">💬</span>
            Your Experience &amp; Suggestions
          </div>

          <label className="pf-label">What kind of feedback is this?</label>
          <div className="pf-type-row">
            {feedbackTypes.map((type) => (
              <button
                type="button"
                key={type.id}
                className={`pf-type-chip ${feedbackType === type.id ? "active" : ""}`}
                onClick={() => setFeedbackType(type.id)}
              >
                <span>{type.icon}</span> {type.label}
              </button>
            ))}
          </div>

          <label className="pf-label">Tell us more</label>
          <textarea
            className="pf-textarea"
            rows="4"
            maxLength={MAX_CHARS}
            placeholder="Type your comment here..."
            value={experienceText}
            onChange={(e) => setExperienceText(e.target.value)}
          />
          <div className="pf-char-count">{experienceText.length}/{MAX_CHARS}</div>
        </div>

        {submitError && <div className="pf-error">{submitError}</div>}

        {showSuccess ? (
          <div className="pf-success">✅ Feedback submitted successfully!</div>
        ) : (
          <button type="submit" className="pf-submit-btn" disabled={submitting}>
            {submitting ? "Submitting..." : "Submit Feedback"}
          </button>
        )}
      </form>
    </div>
  );
};

export default Feedback;